import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { fetchMaintenanceStatus } from "../shared/services/systemSettingsService";

// Compute next Monday at 12:00 local time and keep a live countdown
const getNextMondayNoon = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun,1=Mon,...
  const delta = (1 - day + 7) % 7; // days until Monday
  const target = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + delta,
    12,
    0,
    0,
    0,
  );
  if (target <= now) {
    target.setDate(target.getDate() + 7);
  }
  return target;
};

// Simple standalone maintenance page (no layout, nav, footer)
const MaintenancePage = () => {
  // ALL HOOKS MUST BE AT THE TOP - React Hook Rules
  const [target, setTarget] = useState(getNextMondayNoon());
  const [ready, setReady] = useState(false);
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, getNextMondayNoon() - new Date()),
  );

  // On mount, verify maintenance mode and prime countdown; block UI until verified
  useEffect(() => {
    (async () => {
      try {
        const { maintenanceMode, maintenanceUntil } =
          await fetchMaintenanceStatus();
        if (!maintenanceMode) {
          window.location.replace("/");
          return;
        }
        if (maintenanceUntil) {
          const t = new Date(maintenanceUntil);
          setTarget(t);
          setRemainingMs(Math.max(0, t - new Date()));
        }
      } catch {
        // On error, be safe and allow page to render
      } finally {
        setReady(true);
      }
    })();
  }, []);

  // Countdown timer effect + maintenance mode check
  useEffect(() => {
    const id = setInterval(async () => {
      const diff = target - new Date();
      if (diff <= 0) {
        // Timer expired - check if maintenance mode is still enabled
        try {
          const { maintenanceMode } = await fetchMaintenanceStatus();
          if (!maintenanceMode) {
            // Maintenance mode has been disabled - redirect to home
            window.location.replace("/");
            return;
          }
        } catch {
          // On error, continue with countdown
        }

        const next = getNextMondayNoon();
        setTarget(next);
        setRemainingMs(next - new Date());
      } else {
        setRemainingMs(diff);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [target]);

  // Periodic maintenance mode check (every 30 seconds)
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const { maintenanceMode } = await fetchMaintenanceStatus();
        if (!maintenanceMode) {
          window.location.replace("/");
        }
      } catch {
        // Ignore errors
      }
    };

    const id = setInterval(checkMaintenanceMode, 30000); // Check every 30 seconds
    return () => clearInterval(id);
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen bg-background text-white flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  return (
    <div className="min-h-screen bg-background text-white relative overflow-hidden flex items-center justify-center p-6">
      <Helmet>
        <title>We'll be back soon | Cluck 2.0 Maintenance</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Brand ambient background accents */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 w-[44rem] h-[44rem] rounded-full blur-3xl opacity-20"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #F5B955 0%, rgba(245,185,85,0.1) 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-40 w-[44rem] h-[44rem] rounded-full blur-3xl opacity-20"
        style={{
          background:
            "radial-gradient(circle at 70% 70%, #F5B955 0%, rgba(245,185,85,0.08) 65%)",
        }}
      />

      <div className="relative z-10 w-full max-w-2xl text-center">
        {/* Logo */}
        <img
          src="/Logo-symbol.png"
          alt="Cluck"
          className="mx-auto w-16 h-16 md:w-20 md:h-20 drop-shadow mb-6"
        />

        {/* Card */}
        <div className="bg-background/70 backdrop-blur-xl border border-border rounded-2xl p-6 md:p-8 shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white mb-6 md:mb-8">
            We're migrating to Cluck 2.0
          </h1>
          <img
            src="/key2.png"
            alt=""
            className="mx-auto h-12 md:h-16 w-auto mb-4"
          />

          {/* Countdown to Monday midday */}
          <div className="mb-6">
            <p className="text-muted-foreground mb-3">Unlocking in:</p>
            <div className="mx-auto flex items-stretch justify-center gap-2 md:gap-3">
              {[
                { label: "D", value: days },
                { label: "H", value: hours },
                { label: "M", value: minutes },
                { label: "S", value: seconds },
              ].map((b, i) => (
                <div
                  key={i}
                  // eslint-disable-next-line no-restricted-syntax -- intentional 3-stop brand gradient as 1px border ring
                  className="rounded-xl p-px bg-gradient-to-r from-[#FFE48A] via-[#F5B955] to-[#A87A1F]"
                >
                  <div className="bg-background/80 rounded-xl px-4 py-3 md:px-5 md:py-4 min-w-[3.75rem]">
                    <div className="text-2xl md:text-3xl font-extrabold tabular-nums text-white text-center">
                      {String(b.value).padStart(2, "0")}
                    </div>
                    <div className="text-xs md:text-[0.7rem] text-muted-foreground text-center mt-1">
                      {b.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What's new in Cluck 2.0 */}
          <h2 className="text-lg md:text-xl font-bold text-white mb-3">
            What's new in Cluck 2.0
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 text-foreground text-sm mb-6">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>Improved UX and streamlined navigation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>More game providers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>7‑day rewards calendars</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>Daily VIP rewards</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>All‑new VIP system</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>Weekly bonus purchases</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>Improved notification system</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>Revamped email campaigns</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>Affiliate program</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✓</span>
              <span>And a huge roadmap of features to come</span>
            </li>
          </ul>

          <div className="mx-auto h-px w-48 bg-secondary mb-6" />

          {/* FAQ Section */}
          <h2 className="text-lg md:text-xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4 text-left mb-6">
            <div>
              <h3 className="text-white font-semibold mb-2">
                What's going on?
              </h3>
              <p className="text-foreground text-sm">
                We're migrating to a brand new site with enhanced features,
                improved performance, and a better user experience.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">
                What about my redemptions?
              </h3>
              <p className="text-foreground text-sm">
                Redemptions already submitted will be processed as normal within
                24 hours of submission. New redemptions can be requested once
                the new site is live.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">
                Are you in financial difficulty?
              </h3>
              <p className="text-foreground text-sm">
                Quite the opposite! We've heavily invested in the new site, its
                capabilities, and our future roadmap to provide you with the
                best possible gaming experience.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">
                When is the new site live?
              </h3>
              <p className="text-foreground text-sm">
                Check the countdown above - we'll be back online Monday at
                midday with all the exciting new features.
              </p>
            </div>
          </div>

          <p className="text-foreground">
            We're sorry for any inconvenience. As a thank you for your patience,
            we'll credit your account with a small bonus once we're up and
            running.
          </p>
        </div>

        {/* Subtle footer note (no site footer/nav) */}
        <div className="mt-6 text-sm text-muted-foreground">
          Need help? Please use our live support via the chat widget.
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
